import classNames from 'classnames'
import React, { FC, Fragment, ReactNode, useId } from 'react'
import { Badge } from '../Badge'
import { ScreenLayoutSection } from '../ScreenLayout'
import { Text } from '../Text'
import { useTranslation } from 'react-i18next'
import { cn } from 'utilities/StringUtils'

export interface SettingInputBlockProps {
  classes?: Partial<{
    content: string
  }>
  label?: string
  description?: string | string[] | React.ReactFragment
  children: ReactNode
  labelFor?: string
  isNew?: boolean
  isLoading?: boolean
  loadingSize?: string
  guideButton?: JSX.Element
}

interface SettingInputBlockGroupProps {
  children: ReactNode
}

export const SettingInputBlockGroup: FC<SettingInputBlockGroupProps> = ({ children }) => (
  <div className="space-y-10 divide-y divide-border">{children}</div>
)

export const SettingInputBlock: FC<SettingInputBlockProps> = ({
  classes,
  label,
  description,
  children,
  isNew = false,
  isLoading = false,
  loadingSize = 'sm',
  guideButton,
}) => {
  const id = useId()
  const { t } = useTranslation()
  const descriptionArray = Array.isArray(description)
    ? description
    : [description].filter((v) => !!v)

  return (
    <ScreenLayoutSection>
      <div className="lg:grid lg:grid-cols-3 lg:gap-16">
        <div id={id}>
          {label && (
            <p className="flex items-center font-medium leading-6 text-gray-900 dark:text-gray-100 space-x-2">
              <Text type="headline">{label}</Text>
              {isNew && <Badge variant="info" label={t('badge.new')} />}
            </p>
          )}

          {descriptionArray.length > 0 && (
            <p className="space-y-2 mt-1">
              {descriptionArray.map((descriptionLine: any, index) => (
                <Fragment key={index}>
                  {typeof descriptionLine !== 'string' && React.isValidElement(descriptionLine) ? (
                    <div key={index}>{descriptionLine}</div>
                  ) : (
                    <Text type="caption" key={index}>
                      {descriptionLine}
                    </Text>
                  )}
                </Fragment>
              ))}
            </p>
          )}

          {guideButton && <div className="mt-2">{guideButton}</div>}
        </div>

        <div aria-describedby={id} className={cn('mt-5 lg:mt-0 lg:col-span-2', classes?.content)}>
          {isLoading ? (
            <div
              className={classNames('bg-gray-300 animate-pulse rounded-lg', {
                'h-12': loadingSize === 'sm',
                'h-64': loadingSize === 'lg',
              })}
            />
          ) : (
            children
          )}
        </div>
      </div>
    </ScreenLayoutSection>
  )
}
